import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthHeader from '../layout/AuthHeader';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const navigate = useNavigate();
  const { login, loginWithGoogle, currentUser, isAnonymous, isEmailLink, completeSignInWithEmailLink } = useAuth();

  // Redirect if user is already logged in and not anonymous
  useEffect(() => {
    if (currentUser && !isAnonymous) {
      navigate('/');
    }
  }, [currentUser, isAnonymous, navigate]);

  // Check if the URL is an email sign-in link
  useEffect(() => {
    const checkEmailLink = async () => {
      if (isEmailLink(window.location.href)) {
        setVerifyingEmail(true);
        
        // Get the email from localStorage that we saved when sending the link
        let savedEmail = localStorage.getItem('emailForSignIn');
        
        if (!savedEmail) {
          // If no saved email, prompt the user for it
          savedEmail = window.prompt('Please provide your email for confirmation');
        }
        
        if (savedEmail) {
          try {
            setLoading(true);
            await completeSignInWithEmailLink(savedEmail);
            
            // Get the stored password to create the account
            const pendingPassword = localStorage.getItem('pendingUserPassword');
            if (pendingPassword) {
              // Create account with email/password now that email is verified
              await login(savedEmail, pendingPassword);
              localStorage.removeItem('pendingUserPassword');
            }
            
            navigate('/');
          } catch (error) {
            setError('Error completing sign-in: ' + error.message);
            setVerifyingEmail(false);
          } finally {
            setLoading(false);
          }
        } else {
          setError('No email provided for verification.');
          setVerifyingEmail(false);
        }
      }
    };
    
    checkEmailLink();
  }, [isEmailLink, completeSignInWithEmailLink, login, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await login(email, password);
      navigate('/');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    
    try {
      await loginWithGoogle();
      navigate('/');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (verifyingEmail) {
    return (
      <>
        <AuthHeader />
        <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4 pt-20 transition-colors duration-200">
          <div className="w-full max-w-md p-6 bg-card border border-border rounded-lg shadow-lg text-center">
            {error ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2 className="mt-4 text-2xl font-bold text-foreground">Verification Failed</h2>
                <p className="mt-2 text-destructive">{error}</p>
              </>
            ) : (
              <>
                <svg className="animate-spin mx-auto h-12 w-12 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <h2 className="mt-4 text-2xl font-bold text-foreground">Verifying Your Email</h2>
                <p className="mt-2 text-muted-foreground">Please wait while we complete your registration...</p>
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AuthHeader />
      <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4 pt-20 transition-colors duration-200 select-none">
        <h1 className="text-3xl font-bold text-foreground mb-8 select-none">Log in to your ThatAIHumanizer account</h1>
        
        <div className="w-full max-w-md bg-card border border-border rounded-lg shadow-lg p-6">
          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 text-destructive rounded select-none">
              {error}
            </div>
          )}
          
          {/* Google Login Button */}
          <div className="mb-6">
            <button 
              onClick={handleGoogleLogin}
              disabled={loading}
              type="button"
              className="w-full flex items-center justify-center py-2 px-4 border border-border rounded-full text-foreground bg-card hover:bg-accent disabled:opacity-50 select-none transition-colors"
            >
              <svg className="h-5 w-5 mr-2 select-none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" 
                fill="#4285F4" />
              </svg>
              Continue with Google
            </button>
          </div>
          
          {/* Email & Password Fields */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground"
                placeholder="Email"
                required
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
            </div>
            
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring bg-background text-foreground placeholder:text-muted-foreground"
                placeholder="Password"
                required
                autoComplete="new-password"
                autoCorrect="off"
                spellCheck="false"
              />
              <button 
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <svg className="h-5 w-5 text-muted-foreground select-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-muted-foreground select-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            
            <div className="flex justify-end">
              <a href="/forgot-password" className="text-sm text-primary hover:text-primary/80 select-none transition-colors">
                Forgot password?
              </a>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 border border-transparent rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 transition-colors"
            >
              {loading ? 'Logging in...' : 'Log in'}
            </button>
            
            <div className="text-center mt-3 select-none">
              <a href="/register" className="text-sm text-primary hover:text-primary/80 transition-colors">
                Don't have an account?
              </a>
            </div>
          </form>
        </div>
        
        <div className="mt-4 text-center text-sm text-muted-foreground select-none">
          By continuing, you agree to <Link to="/terms" className="text-primary hover:text-primary/80">Terms of Service</Link> and have read our <Link to="/privacy" className="text-primary hover:text-primary/80">Privacy Policy</Link>.
        </div>
      </div>
    </>
  );
} 